/**
 * ModalCartAction.jsx
 * ─────────────────────────────────────────────────────────
 * BOTTOM SECTION – "Add to Cart" pill + dynamic total price.
 *
 * Renders the black rounded-full button as in the design:
 *   [ ADD TO CART   EGP 100.00  → ]
 *
 * Responsibilities:
 *  - Fires the POST /api/cart/mine/items request
 *  - Updates the global app-context cart count on success
 *  - Calls onSuccess() when the item has been added (so the
 *    parent can close the modal / show a toast)
 *  - Shows a spinner while loading
 *
 * @param {object}   props
 * @param {number}   props.totalPrice   - Calculated total to display
 * @param {string}   props.currency     - Currency code, e.g. 'EGP'
 * @param {boolean}  props.isLoading    - Loading indicator
 * @param {Function} props.onAddToCart  - Async handler that returns a promise
 * @param {boolean}  props.disabled     - Optionally disable the button
 */
/* eslint-disable react/prop-types */
import React from 'react';
import { _ } from '@evershop/evershop/src/lib/locale/translate';
import { useLanguage } from '@components/common/locale/useLanguage';
export default function ModalCartAction(_ref) {
  var {
    totalPrice,
    currency = 'EGP',
    isLoading,
    onAddToCart,
    disabled = false,
    text,
    loadingText = _('Adding...')
  } = _ref;
  var {
    isAr
  } = useLanguage();
  var localizedText = text ? _(text) : _('ADD TO CART');
  var handleClick = () => {
    if (!isLoading && !disabled) {
      onAddToCart();
    }
  };
  return /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "group modal-cart-action-btn".concat(isLoading ? ' modal-cart-action-btn--loading' : '').concat(disabled ? ' modal-cart-action-btn--disabled' : ''),
    onClick: handleClick,
    disabled: isLoading || disabled,
    "aria-label": "".concat(localizedText, " \u2014 ").concat(isAr ? 'جم' : currency, " ").concat(totalPrice)
  }, /*#__PURE__*/React.createElement("span", {
    className: "modal-cart-label font-zoom-pro-compact"
  }, isLoading ? loadingText : localizedText), /*#__PURE__*/React.createElement("div", {
    className: "flex md:gap-[1.8rem] gap-[1rem] items-center"
  }, /*#__PURE__*/React.createElement("span", {
    className: "modal-cart-price !font-rubik"
  }, isAr ? /*#__PURE__*/React.createElement(React.Fragment, null, Number(totalPrice || 0).toFixed(2), " \u062C\u0645") : /*#__PURE__*/React.createElement(React.Fragment, null, currency, " ", Number(totalPrice || 0).toFixed(2))), /*#__PURE__*/React.createElement("div", {
    className: "w-[2.6402rem] h-[2.6402rem] md:w-[4.024rem] md:h-[4.024rem] flex items-center justify-center rounded-full bg-white"
  }, /*#__PURE__*/React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "18",
    height: "15",
    viewBox: "0 0 21 17",
    fill: "none",
    className: "modal-cart-arrow rtl:rotate-180"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M11.2817 16.7285C11.2028 16.6497 11.1402 16.5561 11.0975 16.453C11.0548 16.35 11.0328 16.2396 11.0328 16.128C11.0328 16.0165 11.0548 15.906 11.0975 15.803C11.1402 15.6999 11.2028 15.6063 11.2817 15.5275L17.4728 9.33746L0.847622 9.33746C0.622501 9.33746 0.406601 9.24804 0.247417 9.08885C0.0882316 8.92967 -0.0011963 8.71377 -0.00119629 8.48865C-0.00119628 8.26353 0.0882317 8.04763 0.247417 7.88844C0.406601 7.72926 0.622501 7.63983 0.847622 7.63983L17.4728 7.63983L11.2817 1.44983C11.1224 1.29055 11.033 1.07453 11.033 0.849289C11.033 0.624043 11.1224 0.408023 11.2817 0.248751C11.441 0.089478 11.657 -3.86204e-07 11.8822 -3.77856e-07C12.1075 -3.69508e-07 12.3235 0.089478 12.4828 0.248751L20.1221 7.88811C20.2011 7.96694 20.2637 8.06056 20.3064 8.1636C20.3491 8.26665 20.3711 8.3771 20.3711 8.48865C20.3711 8.60019 20.3491 8.71065 20.3064 8.81369C20.2637 8.91674 20.2011 9.01035 20.1221 9.08919L12.4828 16.7285C12.404 16.8075 12.3103 16.8701 12.2073 16.9128C12.1043 16.9555 11.9938 16.9775 11.8822 16.9775C11.7707 16.9775 11.6602 16.9555 11.5572 16.9128C11.4542 16.8701 11.3605 16.8075 11.2817 16.7285Z",
    className: "fill-black group-disabled:fill-[#7b7b7b]"
  })))));
}